import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback } from 'react';

import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorFormField } from '@/App/tiles/TargetAccumulator/TargetAccumulatorFormField.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput/index.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import {
  selectTargetAccumulatorCurrencyData,
  selectTargetAccumulatorValidation,
} from '@/store/targetAccumulator/selectors.ts';
import { targetAccumulatorSlice } from '@/store/targetAccumulator/slice.ts';

const Notional = () => {
  const tileId = useTargetAccumulatorTileId();

  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { currency, notionalCurrencyIndex } = useAppSelector((state) =>
    selectTargetAccumulatorCurrencyData(state, tileId),
  );
  const notional = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator?.properties.notional);
  const validation = useAppSelector((state) => selectTargetAccumulatorValidation(state, tileId));

  const isCustom = useTargetAccumulatorSelector(
    (targetAccumulator) => targetAccumulator?.properties.customFields?.includes('Amount') ?? false,
  );

  const handleNotionalChanged = useCallback(
    (value: number | undefined) => {
      sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Notional changed', isDefined(value) ? String(value) : 'null');

      return checkTargetAccumulatorWithTradeCapture({
        notional: isDefined(value) ? value : null,
      });
    },
    [checkTargetAccumulatorWithTradeCapture, sgwtWebAnalytics],
  );

  const handleNotionalCurrencyClick = () => {
    const currencyIndex = notionalCurrencyIndex === 1 ? 2 : 1;

    dispatch(targetAccumulatorSlice.actions.notionalCurrencyChanged({ tileId, notionalCurrencyIndex: currencyIndex }));

    return checkTargetAccumulatorWithTradeCapture({
      notionalCurrencyIndex: currencyIndex,
    });
  };

  return (
    <TargetAccumulatorFormField formattedMessageId="targetAccumulator.form.notional">
      <TradeCaptureValidationWrapper validation={validation?.notional}>
        <SgAmountInput
          value={notional === null ? undefined : notional}
          onChange={handleNotionalChanged}
          unit={currency}
          allowNegative={false}
          className={isCustom ? 'text-info' : undefined}
          data-e2e-is-modified={isCustom}
          unitBackgroundColor={notionalCurrencyIndex === 2 ? 'warning' : undefined}
          onUnitClick={handleNotionalCurrencyClick}
          size="sm"
          selectAllOnFocus
          data-e2e="notional"
        />
      </TradeCaptureValidationWrapper>
    </TargetAccumulatorFormField>
  );
};

export default Notional;
